/**
 * ORÇAMENTO DE TEMPO DO BOOT — onde vão os segundos de preto (2026-07-28).
 *
 * <p>POR QUE EXISTE. A reciclagem por wrap de loop (vide `ramReload`) custa preto na tela, e o
 * requisito do Carlos é 1-2 s. O medido na QB50B foi ~2,24 s, decomposto assim:
 *
 * <pre>
 *   A) decisão → location.reload()          200 ms   (constante; zerada em 2026-07-28)
 *   B) reload  → JS vivo                  1.180 ms   ← 53% do total, e CAIXA-PRETA
 *   C) JS vivo → 1º frame                   860 ms   (prepare ~295 + frame ~560)
 * </pre>
 *
 * <p>O bloco B era estimativa: o projeto não tinha NENHUMA instrumentação de navigation timing
 * (zero `performance.mark`, zero `performance.timing`). Sem quebrar o B não há como decidir se vale
 * atacar o bundle de 1,42 MB — que é o suspeito, mas suspeito não é medição.
 *
 * <p>CUSTO. `performance.now()` é barato e as marcas ficam em MEMÓRIA; uma única escrita de
 * localStorage acontece no fim do boot (`flushBootTiming`). Nada roda por frame. Escrita síncrona de
 * localStorage é lenta na Tizen 6.5 e já é suspeita na degradação de sessão longa — por isso 1 só.
 *
 * <p>ZERO API de plataforma (só `performance` + localStorage) → não viola o desacoplamento do
 * Semgrep.
 */

/**
 * Pontos do boot. A ordem aqui é a ordem esperada na linha do tempo, e é o que permite ler o
 * relatório como uma cascata de deltas.
 */
export type BootMark =
  /** Primeira linha do bundle executando — fim do fetch/parse, início do exec. */
  | 'bundle'
  /** `ReactDOM.render` chamado — o custo de exec do bundle já foi pago. */
  | 'react'
  /** Playlist não-vazia conhecida — o player já sabe O QUE tocar. */
  | 'playlist'
  /** `open()` do AVPlay chamado — começou o trabalho do decoder. */
  | 'open'
  /** 1º frame na tela — o preto acabou. */
  | 'frame';

const TIMING_KEY = 'efflux_boot_timing_v1';
const TIMING_MAX = 20;

/** Marcas em ms desde o início da navegação (origem do `performance.now()`). */
const marks: Partial<Record<BootMark, number>> = {};

/**
 * Registra um ponto do boot. Idempotente: só a PRIMEIRA vez conta.
 *
 * <p>A idempotência não é detalhe — `setPlaylist` roda a cada sync e `open()` a cada slot. Sem ela,
 * as marcas viravam "última vez que aconteceu" em vez de "quando aconteceu no boot", e o orçamento
 * do preto (que é sobre o boot) ficaria sem sentido.
 */
export function markBoot(name: BootMark): void {
  if (marks[name] !== undefined) return;
  try {
    marks[name] = Math.round(performance.now());
  } catch {
    /* sem performance (ambiente de teste) — telemetria é best-effort */
  }
}

export interface BootTimingEntry {
  /** Epoch do fim do boot, pra cruzar com `reload_log` e `session_restart`. */
  t: number;
  /** Epoch do início da navegação — é o que amarra este boot ao reload que o causou. */
  navStart: number | null;
  /** HTML na mão (fim da resposta), em ms desde navStart. */
  html: number | null;
  /** DOM interativo, em ms desde navStart. */
  domInteractive: number | null;
  /** Marcas da app, em ms desde navStart. */
  bundle: number | null;
  react: number | null;
  playlist: number | null;
  open: number | null;
  frame: number | null;
}

function readNavigation(): {
  navStart: number | null;
  html: number | null;
  domInteractive: number | null;
} {
  try {
    // Navigation Timing Level 2 — existe no Chromium 85 (Tizen 6.5).
    const nav = performance.getEntriesByType?.('navigation')?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    const origin =
      typeof performance.timeOrigin === 'number'
        ? performance.timeOrigin
        : null;
    if (nav) {
      return {
        navStart: origin != null ? Math.round(origin) : null,
        html: Math.round(nav.responseEnd),
        domInteractive: Math.round(nav.domInteractive),
      };
    }
    return {
      navStart: origin != null ? Math.round(origin) : null,
      html: null,
      domInteractive: null,
    };
  } catch {
    return { navStart: null, html: null, domInteractive: null };
  }
}

/** O orçamento deste boot, pronto pra ler (HUD / RemoteOps / testes). */
export function getBootTiming(): BootTimingEntry {
  const nav = readNavigation();
  return {
    t: Date.now(),
    navStart: nav.navStart,
    html: nav.html,
    domInteractive: nav.domInteractive,
    bundle: marks.bundle ?? null,
    react: marks.react ?? null,
    playlist: marks.playlist ?? null,
    open: marks.open ?? null,
    frame: marks.frame ?? null,
  };
}

let flushed = false;

/**
 * Grava o orçamento no ring. Chamar UMA vez, quando o 1º frame chegar — antes disso o retrato está
 * incompleto, e depois disso nada muda (as marcas são idempotentes).
 *
 * <p>Ring persistente pelo mesmo motivo do `black_log`: cada reciclagem é um boot novo, então
 * qualquer coisa guardada por sessão seria sobrescrita e a série de medições se perderia.
 */
export function flushBootTiming(): void {
  if (flushed) return;
  flushed = true;
  try {
    const raw = window.localStorage?.getItem(TIMING_KEY);
    const list: BootTimingEntry[] = raw
      ? (JSON.parse(raw) as BootTimingEntry[])
      : [];
    list.push(getBootTiming());
    window.localStorage?.setItem(
      TIMING_KEY,
      JSON.stringify(list.slice(-TIMING_MAX)),
    );
  } catch {
    /* best-effort — telemetria nunca quebra playback */
  }
}

/** Histórico dos últimos boots (diagnóstico/testes). */
export function getBootTimingLog(): BootTimingEntry[] {
  try {
    const raw = window.localStorage?.getItem(TIMING_KEY);
    return raw ? (JSON.parse(raw) as BootTimingEntry[]) : [];
  } catch {
    return [];
  }
}

/**
 * `bundle` é marcado NA AVALIAÇÃO DESTE MÓDULO, não por quem importa.
 *
 * <p>Imports ESM são hoisted: um `markBoot('bundle')` escrito no `index.tsx` entre os imports
 * rodaria só depois de React, App e todo o resto já terem sido avaliados — mediria ~0 e mentiria
 * justamente sobre o pedaço que interessa. Marcando aqui, a marca cai no instante em que o 1º
 * import da app é avaliado, que é o começo real da execução do bundle.
 */
markBoot('bundle');

/** Só pra teste — marcas e flush são estado de módulo. */
export function __resetBootTimingForTest(): void {
  (Object.keys(marks) as BootMark[]).forEach((k) => delete marks[k]);
  flushed = false;
}
